import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Clock3, Route, UsersRound, X, Plus, Pencil } from "lucide-react";
import InternalExtraScheduleModal from './InternalExtraScheduleModal';
import ExtraScheduleReceipt, { receiptLabel } from './ExtraScheduleReceipt';
import { BACKEND_URL as API } from "../../apiConfig";
import { getInternalFleetCopy } from "../../pages/internal/internalFleetI18n";

const TIME_AXIS_HOURS = ["06:00", "09:00", "12:00", "15:00", "18:00", "22:00"];
const TIMELINE_START_HOUR = 6;
const TIMELINE_END_HOUR = 22;
const INTERNAL_TIMELINE_BUFFER_HOURS = 1;

export default function FleetDispatchOverview({ language = "zh" }) {
  const t = getInternalFleetCopy(language).dispatch;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editing, setEditing] = useState(null);
  const [revision, setRevision] = useState(0);
  const [dateFrom, setDateFrom] = useState(() => formatInternalLocalDate(new Date()));
  const [dateTo, setDateTo] = useState(() => { const end = new Date(); end.setDate(end.getDate() + 30); return formatInternalLocalDate(end); });
  const [extraSchedules, setExtraSchedules] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    async function fetchOverview() {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/api/internal/dispatch/overview`, { params: { date_from: dateFrom, date_to: dateTo } });
        const extras = await axios.get(`${API}/api/internal/dispatch/extra-schedules`, { params: { date_from: dateFrom, date_to: dateTo } });
        if (!active) return;
        setData(response.data);
        setExtraSchedules(extras.data);
        setError('');
      } catch (error) {
        if (!active) return;
        console.error("Failed to fetch internal dispatch overview:", error);
        setError('排班加载失败，请重试');
      } finally {
        if (active) setLoading(false);
      }
    }
    fetchOverview();
    return () => { active = false; };
  }, [dateFrom, dateTo, revision]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-amber-300/60 border-t-transparent" />
      </div>
    );
  }

  const totals = data?.totals || {};
  const drivers = data?.drivers || [];
  const timeline = data?.timeline || [];
  const timelineDays = buildInternalTimelineDays(data?.date_from);

  return (
    <div className="space-y-6" data-testid="internal-dispatch-overview">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">开始日期<input aria-label="开始日期" type="date" className="ml-2 rounded-md bg-black p-2" value={dateFrom} max={dateTo} onChange={e => e.target.value && setDateFrom(e.target.value)} /></label>
        <label className="text-sm">结束日期<input aria-label="结束日期" type="date" className="ml-2 rounded-md bg-black p-2" value={dateTo} min={dateFrom} onChange={e => e.target.value && setDateTo(e.target.value)} /></label>
        <button type="button" onClick={() => setEditing({})} className="flex items-center gap-2 rounded-md bg-amber-200 px-4 py-2 font-semibold text-black"><Plus size={18} />追加订单</button>
      </div>
      {error && <div role="alert">{error}<button type="button" className="ml-3 underline" onClick={() => setRevision(v => v + 1)}>重试</button></div>}
      <section className="space-y-3" aria-label="内部调度追加排班">
        {extraSchedules.map(schedule => <div key={schedule.id} className="flex flex-col gap-3 border-b border-amber-300/20 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 space-y-1 break-words">
            <p className="font-semibold">调度追加 · {schedule.created_by_name || '未登记'} · {schedule.driver_name}</p>
            <p>{schedule.date} {schedule.time} · {schedule.pickup_location} → {schedule.dropoff_location}</p>
            <p>{schedule.price_amount?.toLocaleString()} {schedule.price_currency} · <ExtraScheduleReceipt schedule={schedule} /></p>
          </div>
          <button type="button" disabled={schedule.status === 'cancelled'} onClick={() => setEditing(schedule)} className="flex shrink-0 items-center gap-2 rounded-md border border-amber-300/30 px-3 py-2 disabled:opacity-50"><Pencil size={16} />修改</button>
        </div>)}
      </section>
      <div className="internal-luxury-card overflow-hidden p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="premium-chip border-amber-300/30 text-[#f3d98b]">{t.chip}</span>
            <h2 className="mt-3 text-2xl font-semibold text-white">{t.title}</h2>
            <p className="mt-1 text-sm text-slate-400">
              {t.range} / {data?.date_from || "-"} - {data?.date_to || "-"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <DispatchStat icon={UsersRound} label={t.driverTotal} value={totals.drivers || 0} />
            <DispatchStat icon={CalendarDays} label={t.scheduledDrivers} value={totals.scheduled_drivers || 0} />
            <DispatchStat icon={Route} label={t.officialOrders} value={totals.official || 0} />
            <DispatchStat icon={Clock3} label={t.extraSchedules} value={totals.extras || 0} />
          </div>
        </div>
      </div>

      <section className="space-y-4" data-testid="internal-dispatch-timeline-board">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-base font-semibold text-[#f4e6bd]">{t.overview}</h3>
            <p className="mt-1 text-sm text-slate-500">{t.timeline}</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full border border-[#ffe7a3]/55 bg-[#d8b76a]/18 px-3 py-1 text-[#ffeec0]">{t.official}</span>
            <span className="rounded-full border border-[#caa24d]/35 bg-[#3a2a10]/70 px-3 py-1 text-[#e7c66f]">{t.driverExtra}</span>
            <span className="rounded-full border border-[#8a6a24]/45 bg-[#20170b]/80 px-3 py-1 text-[#cfa85a]">{t.dispatcherExtra}</span>
          </div>
        </div>

        {drivers.length === 0 ? (
          <div className="rounded-xl border border-[#d8b76a]/15 bg-black/25 p-8 text-center text-sm text-slate-500">
            {t.emptyTimeline}
          </div>
        ) : (
          drivers.map((driver) => (
            <InternalDriverTimelineCard
              key={driver.driver_id}
              driver={driver}
              days={timelineDays}
              timeline={timeline}
              t={t}
              onOpenItem={setSelectedItem}
            />
          ))
        )}
      </section>

      {selectedItem && (
        <InternalDispatchItemDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onEdit={schedule => { setSelectedItem(null); setEditing(schedule); }}
        />
      )}
      {editing && <InternalExtraScheduleModal drivers={drivers} schedule={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={schedule => {
        setEditing(null);
        if (schedule.date < dateFrom || schedule.date > dateTo) { setDateFrom(schedule.date); setDateTo(schedule.date); }
        setRevision(v => v + 1);
      }} />}
    </div>
  );
}

function DispatchStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-amber-300/20 bg-black/25 px-4 py-3">
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Icon className="h-4 w-4 text-[#f3d98b]" />
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-white">{value}</div>
    </div>
  );
}

function InternalDriverTimelineCard({ driver, days, timeline, t, onOpenItem }) {
  const driverItems = timeline.filter((item) => item.driver_id === driver.driver_id);
  const totalItems = driver.total_count || driverItems.length || 0;
  const recommendation = totalItems === 0
    ? { text: t.noSchedule, className: "border-[#d8b76a]/20 bg-[#1d1609] text-[#d8b76a]" }
    : totalItems <= 2
      ? { text: t.scheduledDrivers, className: "border-[#d8b76a]/30 bg-[#d8b76a]/12 text-[#f3d98b]" }
      : { text: t.officialOrders, className: "border-[#ffe7a3]/35 bg-[#d8b76a]/14 text-[#ffe6a6]" };

  return (
    <div className="overflow-hidden rounded-2xl border border-[#d8b76a]/22 bg-[linear-gradient(180deg,rgba(18,15,10,0.98),rgba(6,5,3,0.98))] shadow-[0_18px_60px_rgba(0,0,0,0.38)]" data-testid="internal-driver-timeline-card">
      <div className="flex flex-col gap-3 border-b border-[#d8b76a]/18 bg-[linear-gradient(90deg,rgba(32,24,10,0.72),rgba(8,7,5,0.72))] p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#d8b76a]/25 bg-black/45 text-sm font-semibold text-[#f3d98b]">
            {driver.driver_name?.charAt(0) || "?"}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="truncate text-sm font-semibold text-white">{driver.driver_name}</h4>
              <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${recommendation.className}`}>
                {recommendation.text}
              </span>
            </div>
            <p className="mt-1 truncate text-xs text-slate-500">
              {driver.driver_phone || "-"} · {driver.vehicle_plate || t.noVehicle || "-"} · {t.official} {driver.official_count || 0} · {t.extraSchedules} {(driver.driver_extra_count || 0) + (driver.dispatcher_extra_count || 0)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded-full border border-[#ffe7a3]/35 bg-[#d8b76a]/12 px-3 py-1 text-[#ffe6a6]">{t.official} {driver.official_count || 0}</span>
          <span className="rounded-full border border-[#caa24d]/25 bg-[#2a1f0d] px-3 py-1 text-[#d8b76a]">{t.driverExtra} {driver.driver_extra_count || 0}</span>
          <span className="rounded-full border border-[#8a6a24]/35 bg-black/35 px-3 py-1 text-[#c49a4a]">{t.dispatcherExtra} {driver.dispatcher_extra_count || 0}</span>
        </div>
      </div>

      <div className="space-y-3 p-4">
        {days.map((day) => (
          <InternalDriverDayTimeline
            key={day.date}
            day={day}
            items={driverItems.filter((item) => item.date === day.date)}
            t={t}
            onOpenItem={onOpenItem}
          />
        ))}
      </div>
    </div>
  );
}

function InternalDriverDayTimeline({ day, items, t, onOpenItem }) {
  const isFree = items.length === 0;
  const availableText = getInternalAvailableWindowText(items);

  return (
    <div className="grid gap-3 rounded-xl border border-[#d8b76a]/18 bg-[linear-gradient(180deg,rgba(13,11,7,0.98),rgba(4,4,3,0.98))] p-3 lg:grid-cols-[108px_1fr_132px] lg:items-center">
      <div>
        <div className="text-sm font-semibold text-[#f4e6bd]">{day.label}</div>
        <div className="text-xs text-slate-500">周{day.weekday}</div>
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between text-[11px] font-semibold text-[#9f8750]">
          {TIME_AXIS_HOURS.map((hour) => (
            <span key={hour}>{hour}</span>
          ))}
        </div>
        <div className="overflow-x-auto pb-1">
          <div className="relative h-12 min-w-[720px] overflow-hidden rounded-lg border border-[#d8b76a]/25 bg-[linear-gradient(180deg,rgba(24,20,12,0.96),rgba(7,6,4,0.98))] shadow-inner shadow-black/60">
            <div className="absolute inset-y-0 left-0 right-0 grid grid-cols-5">
              {TIME_AXIS_HOURS.slice(0, -1).map((hour) => (
                <div key={hour} className="border-r border-[#d8b76a]/14 last:border-r-0" />
              ))}
            </div>
            {isFree ? (
              <div className="absolute inset-1 flex items-center justify-center rounded-md border border-[#d8b76a]/20 bg-[#2a1f0d]/70 text-xs font-semibold text-[#d8b76a]">
                {t.noSchedule}
              </div>
            ) : (
              items.map((item, index) => (
                <InternalTimelineBookingBlock
                  key={`${item.type}-${item.id}-${index}`}
                  item={item}
                  index={index}
                  t={t}
                  onOpenItem={onOpenItem}
                />
              ))
            )}
          </div>
        </div>
        <p className="mt-1 text-[11px] text-slate-600">{t.next}</p>
      </div>
      <div className="flex items-center justify-start lg:justify-end">
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${isFree ? "border-[#d8b76a]/22 bg-[#1f170a] text-[#d8b76a]" : "border-[#ffe7a3]/24 bg-[#d8b76a]/12 text-[#f3d98b]"}`}>
          {availableText}
        </span>
      </div>
    </div>
  );
}

function InternalTimelineBookingBlock({ item, index, t, onOpenItem }) {
  const visualRange = getInternalTimelineVisualRange(item);
  const start = getInternalTimelinePercentFromMinutes(visualRange?.startMinutes);
  const end = getInternalTimelinePercentFromMinutes(visualRange?.endMinutes);
  const width = Math.max(4, end - start);
  const top = 5 + (index % 2) * 18;
  const typeClass = item.type === "driver_extra"
    ? "border border-[#f3d98b]/55 bg-[#8a6a24] text-[#fff7dc] shadow-[0_0_18px_rgba(216,183,106,0.16)]"
    : item.type === "dispatcher_extra"
      ? "border border-[#d8b76a]/45 bg-[#5a4316] text-[#fff1bf] shadow-[0_0_18px_rgba(216,183,106,0.13)]"
      : "border border-[#ffe7a3]/65 bg-[linear-gradient(135deg,#f2d77d,#b68b32)] text-[#141006] shadow-[0_0_20px_rgba(216,183,106,0.22)]";
  const summary = `${item.time || "--:--"} ${compactInternalRoute(item) || t.sources[item.type] || item.type}`;

  return (
    <button
      type="button"
      className={`absolute rounded-md px-2 py-0.5 text-left text-[11px] font-semibold shadow-sm ${typeClass}`}
      style={{
        left: `${start}%`,
        top: `${top}px`,
        width: `max(${Math.min(width, 96 - start)}%, 132px)`,
        maxWidth: `calc(100% - ${start}%)`,
      }}
      title={`${item.date} ${item.time || "--:--"} / ${item.route || ""}`}
      data-testid="internal-dispatch-timeline-block"
      onClick={() => onOpenItem?.(item)}
    >
      <span className="block truncate">{summary}</span>
    </button>
  );
}

function InternalDispatchItemDetailModal({ item, onClose, onEdit }) {
  const isOfficialOrder = item?.type === "official";
  const detail = isOfficialOrder ? item.order_detail || item : item.schedule_detail || item;
  const assignedDriver = detail.assigned_driver || {};
  const assignedFleet = detail.assigned_fleet || {};
  const assignedVehicle = detail.assigned_vehicle || {};
  const route = isOfficialOrder ? formatInternalOrderDetailRoute(detail) : formatInternalExtraDetailRoute(detail);
  const notes = [
    detail.trip_details,
    detail.other_service_desc,
    detail.dispatch_note,
    detail.remarks,
    detail.admin_memo,
    detail.notes,
  ].filter(Boolean).join("\n\n");
  const rows = [
    ["类型", isOfficialOrder ? "正式订单" : getInternalDispatchSourceLabel(item.type)],
    ["创建账号", detail.created_by_name || "-"],
    ["订单号/编号", detail.order_no || detail.confirmation_code || detail.id || item.id || "-"],
    ["状态", detail.status || item.status || "-"],
    ["日期时间", [detail.departure_date || detail.date || item.date, detail.pickup_time || detail.time || item.time].filter(Boolean).join(" ") || "-"],
    ["客户名称", detail.customer_name || item.customer_name || "-"],
    ["客户代码", detail.customer_code || "-"],
    ["客户邮箱", detail.customer_email || detail.user_email || "-"],
    ["电话", detail.phone || detail.customer_phone || detail.customer_contact || "-"],
    ["服务类型", detail.service_type || item.service_type || "-"],
    ["车辆类型", detail.vehicle_type || "-"],
    ["路线", route || item.route || "-"],
    ["航班", detail.flight_number || "-"],
    ["乘客/行李/儿童座椅/婴儿座椅", `${detail.passenger_count ?? '-'} 人 / ${detail.luggage_count ?? '-'} 件 / ${detail.child_seat_count ?? '-'} 个 / ${detail.infant_seat_count ?? '-'} 个`],
    ["包车小时", detail.selected_hours || detail.duration_hours || item.selected_hours || item.duration_hours || "-"],
    ["订单金额", isOfficialOrder ? (detail.price != null ? formatInternalDispatchMoney(detail.price) : "-") : `${detail.price_amount ?? '-'} ${detail.price_currency || ''}`],
    ["付款状态", isOfficialOrder ? (detail.payment_status || (detail.payment_already_received ? "paid" : "-")) : receiptLabel(detail.receipt_status)],
    ["付款方式", detail.payment_method || "-"],
    ["司机代收", detail.driver_collection_amount ? `${formatInternalDispatchMoney(detail.driver_collection_amount)} ${detail.driver_collection_currency || "JPY"}` : "-"],
    ["司机", assignedDriver.driver_name || detail.assigned_driver_name || item.driver_name || "-"],
    ["司机电话", assignedDriver.driver_phone || detail.assigned_driver_phone || detail.driver_phone || item.driver_phone || "-"],
    ["车队", assignedFleet.fleet_name || assignedDriver.fleet_name || "-"],
    ["车辆", [
      assignedVehicle.plate_number,
      assignedDriver.plate_number,
      detail.vehicle_plate,
      detail.assigned_vehicle_id,
    ].filter(Boolean)[0] || "-"],
    ["派单状态", detail.driver_assignment_status || "-"],
    ["司机端状态", detail.driver_order_status || "-"],
    ["创建时间", detail.created_at || "-"],
    ["更新时间", detail.updated_at || "-"],
  ];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/72 p-4" data-testid="internal-dispatch-item-detail-modal">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-[#d8b76a]/35 bg-[#070707] shadow-2xl shadow-black/70">
        <div className="flex items-start justify-between gap-3 border-b border-[#d8b76a]/20 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d8b76a]">司机安排情况</p>
            <h3 className="mt-1 truncate text-lg font-semibold text-white">
              {detail.order_no || detail.confirmation_code || detail.id || item.id || "订单详情"}
            </h3>
            <p className="mt-1 truncate text-xs text-slate-400">{route || item.route || "-"}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[#d8b76a]/25 p-2 text-[#d8b76a] transition hover:bg-[#d8b76a]/10"
            aria-label="关闭订单详情"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-auto p-5">
          {detail.creation_origin === 'internal' && detail.status !== 'cancelled' && <button type="button" className="mb-4 flex items-center gap-2 rounded-md border border-amber-300/30 px-3 py-2" onClick={() => onEdit(detail)}><Pencil size={16} />修改调度追加</button>}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map(([label, value]) => (
              <div key={label} className="min-h-[76px] rounded-md border border-[#d8b76a]/15 bg-black/35 px-3 py-2">
                <p className="text-[11px] font-semibold text-[#9f8750]">{label}</p>
                <p className="mt-1 break-words text-sm font-medium text-slate-100">{value || "-"}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-md border border-[#d8b76a]/15 bg-black/35 px-3 py-3">
            <p className="text-[11px] font-semibold text-[#9f8750]">备注/行程详情</p>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-100">{notes || "-"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatInternalOrderDetailRoute(detail) {
  const pickup = detail.pickup_location_detail || detail.pickup_location || detail.pickup_address || detail.pickup_area || "";
  const dropoff = detail.dropoff_location_detail || detail.destination || detail.destination_address || detail.dropoff_location || detail.dropoff_area || "";
  return pickup && dropoff ? `${pickup} → ${dropoff}` : pickup || dropoff || "";
}

function formatInternalExtraDetailRoute(detail) {
  const pickup = detail.pickup_location || "";
  const dropoff = detail.dropoff_location || "";
  return pickup && dropoff ? `${pickup} → ${dropoff}` : pickup || dropoff || "";
}

function formatInternalDispatchMoney(value) {
  const numberValue = Number(value || 0);
  return `¥${numberValue.toLocaleString("ja-JP", { maximumFractionDigits: 0 })}`;
}

function getInternalDispatchSourceLabel(type) {
  if (type === "driver_extra") return "司机追加日程";
  if (type === "dispatcher_extra") return "调度追加日程";
  return type || "-";
}

function buildInternalTimelineDays(dateFrom) {
  const start = dateFrom ? new Date(`${dateFrom}T00:00:00`) : new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return [0, 1, 2].map((offset) => {
    const date = new Date(start);
    date.setDate(start.getDate() + offset);
    const diff = Math.round((date.setHours(0, 0, 0, 0) - today) / 86400000);
    const label = diff === 0 ? "今天" : diff === 1 ? "明天" : diff === 2 ? "后天" : `${date.getMonth() + 1}/${date.getDate()}`;
    return {
      date: formatInternalLocalDate(date),
      label,
      weekday: ["日", "一", "二", "三", "四", "五", "六"][date.getDay()],
    };
  });
}

function formatInternalLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getInternalTimelineMinutes(time) {
  if (!time) return null;
  const [hourText, minuteText = "0"] = String(time).split(":");
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (Number.isNaN(hour)) return null;
  return hour * 60 + (Number.isNaN(minute) ? 0 : minute);
}

function getInternalTimelineDurationHours(item) {
  if (item?.service_type === "charter") {
    const selectedHours = Number(item.selected_hours || item.duration_hours);
    return Number.isFinite(selectedHours) && selectedHours > 0 ? selectedHours : 8;
  }
  const durationHours = Number(item?.duration_hours);
  return Number.isFinite(durationHours) && durationHours > 0 ? durationHours : 2;
}

function getInternalTimelineVisualRange(item) {
  const startMinutes = getInternalTimelineMinutes(item?.time);
  if (startMinutes === null) return null;
  const bufferMinutes = INTERNAL_TIMELINE_BUFFER_HOURS * 60;
  const durationMinutes = getInternalTimelineDurationHours(item) * 60;
  return {
    startMinutes: startMinutes - bufferMinutes,
    endMinutes: startMinutes + durationMinutes + bufferMinutes,
  };
}

function getInternalTimelinePercentFromMinutes(minutes) {
  if (minutes === null || minutes === undefined) return 0;
  const decimalHour = minutes / 60;
  const clamped = Math.max(TIMELINE_START_HOUR, Math.min(TIMELINE_END_HOUR, decimalHour));
  return ((clamped - TIMELINE_START_HOUR) / (TIMELINE_END_HOUR - TIMELINE_START_HOUR)) * 100;
}

function getInternalAvailableWindowText(items) {
  if (items.length === 0) return "06:00-22:00";
  const latestHour = items.reduce((latest, item) => {
    const visualRange = getInternalTimelineVisualRange(item);
    if (!visualRange) return latest;
    return Math.max(latest, visualRange.endMinutes / 60);
  }, TIMELINE_START_HOUR);
  if (latestHour >= TIMELINE_END_HOUR) return "已排满";
  const roundedHour = Math.floor(latestHour);
  const roundedMinute = latestHour % 1 >= 0.5 ? "30" : "00";
  return `${String(roundedHour).padStart(2, "0")}:${roundedMinute}后可派`;
}

function compactInternalRoute(item) {
  const route = item?.route;
  if (!route) return "";
  const normalized = String(route).replace(/\s+/g, " ").trim();
  const parts = normalized.split("→").map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const fromArea = inferInternalRouteArea(parts[0]);
    const toArea = inferInternalRouteArea(parts[parts.length - 1]);
    if (item?.service_type === "charter") {
      return `${fromArea || toArea || "区域"}包车`;
    }
    if (fromArea || toArea) {
      return `${fromArea || compactInternalLocation(parts[0])}-${toArea || compactInternalLocation(parts[parts.length - 1])}`;
    }
    return `${compactInternalLocation(parts[0])}-${compactInternalLocation(parts[parts.length - 1])}`;
  }
  const area = inferInternalRouteArea(normalized);
  if (item?.service_type === "charter" && area) {
    return `${area}包车`;
  }
  return area || compactInternalLocation(normalized);
}

function compactInternalLocation(value) {
  const first = String(value || "")
    .split(/[、,，/／|｜]/)[0]
    .split(/\s+/)[0]
    .split("　")[0]
    .trim();
  return first.length > 8 ? `${first.slice(0, 8)}...` : first;
}

function inferInternalRouteArea(value) {
  const text = String(value || "").toLowerCase();
  const areaRules = [
    { label: "成田", patterns: ["narita", "nrt", "成田"] },
    { label: "羽田", patterns: ["haneda", "hnd", "羽田"] },
    { label: "东京", patterns: ["tokyo", "東京", "新宿", "銀座", "ginza", "shinjuku", "渋谷", "shibuya", "浅草", "asakusa", "上野", "ueno", "品川", "shinagawa", "丸の内", "marunouchi", "六本木", "roppongi", "お台場", "odaiba", "舞浜", "maihama", "disney"] },
    { label: "横滨", patterns: ["yokohama", "横浜"] },
    { label: "箱根", patterns: ["hakone", "箱根", "強羅", "gora", "小田原", "odawara"] },
    { label: "日光", patterns: ["nikko", "日光", "東照宮", "toshogu"] },
    { label: "川越", patterns: ["kawagoe", "川越"] },
    { label: "富士", patterns: ["fuji", "富士", "河口湖", "kawaguchiko", "山中湖", "yamanakako"] },
    { label: "静冈", patterns: ["shizuoka", "静岡", "清水", "shimizu"] },
    { label: "大阪", patterns: ["osaka", "大阪"] },
    { label: "京都", patterns: ["kyoto", "京都"] },
    { label: "名古屋", patterns: ["nagoya", "名古屋"] },
  ];
  const matched = areaRules.find((rule) => rule.patterns.some((pattern) => text.includes(pattern.toLowerCase())));
  return matched?.label || "";
}
